import { Button, Layout } from 'antd'
import Link from 'next/link'
import { FunctionComponent, ReactNode } from 'react'
import styled from 'styled-components'
import LogoHorizontal from '@/components/common/logo-horizontal'
import LogoIcon from '@/components/common/logo-icon'
import ScrollArea from '@/components/common/scroll-area'
import CtaButton from '@/components/extension/cta-button'
import useDisplayChromeExtensionCtaButton from '@/components/extension/use-display-chrome-extension-cta-button'
import CollapseIconSVG from '@/components/icon/own-svg/collapse-icon.svg'
import PinIconSVG from '@/components/icon/own-svg/pin-icon.svg'
import {
  SIDER_COLLAPSED_WIDTH,
  SIDER_EXPANDED_WIDTH,
} from '@/components/layout/irm/irm-layout'
import useDefaultPage from '@/hooks/use-authorization/use-default-page'

const { Sider } = Layout

type IrmSiderProps = {
  children: ReactNode
  isHoverSider: boolean
  isSiderCollapsed: boolean
  toggleSiderCollapse: () => void
}

const IrmSider: FunctionComponent<IrmSiderProps> = ({
  children,
  isHoverSider,
  isSiderCollapsed,
  toggleSiderCollapse,
}) => {
  const defaultPage = useDefaultPage()
  const displayExtensionCtaButton = useDisplayChromeExtensionCtaButton()
  const isCollapsed = isSiderCollapsed && !isHoverSider

  return (
    <StyledSider
      collapsible
      collapsed={isCollapsed}
      collapsedWidth={SIDER_COLLAPSED_WIDTH}
      trigger={null}
      width={SIDER_EXPANDED_WIDTH}
    >
      <LogoWrapper isCollapsed={isCollapsed}>
        <Link href={defaultPage}>
          {isCollapsed ? <LogoIcon /> : <LogoHorizontal />}
        </Link>
        <ToggleCollapsedButton
          $isCollapsed={isCollapsed}
          icon={
            isSiderCollapsed && isHoverSider ? (
              <PinIconSVG />
            ) : (
              <CollapseIconSVG />
            )
          }
          type='text'
          onClick={toggleSiderCollapse}
        />
      </LogoWrapper>
      <ChildrenWrapper>
        <ScrollArea>
          {children}
          {displayExtensionCtaButton && (
            <>
              {/** 留下一個可以 Scroll 的空間，避免 CtaButton 蓋住元素的情況 */}
              <div style={{ height: 132 }} />
              <CtaButton isCollapsed={isCollapsed} />
            </>
          )}
        </ScrollArea>
      </ChildrenWrapper>
    </StyledSider>
  )
}

const StyledSider = styled(Sider)`
  box-shadow: 0 2px 12px rgb(0, 0, 0, 0.102);
  text-align: left;

  ${CtaButton} {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 40px 16px 16px 16px;
    background: linear-gradient(
      to top,
      rgb(255, 255, 255, 1) 0%,
      rgb(255, 255, 255, 1) 70%,
      rgb(255, 255, 255, 0) 100%
    );
  }

  .ant-menu-item {
    width: 100%;
    margin: 0;
  }

  .ant-menu-submenu-title {
    width: 100%;
    margin: 0;
    padding: 0 24px;
    .anticon + span {
      margin-inline-start: 8px;
    }
    .ant-menu-submenu-arrow {
      right: 8px;
    }
  }

  .ant-menu-title-content {
    &,
    & > a {
      display: flex;
      white-space: pre-line;
      line-height: 1.4;
    }
  }

  .ant-menu-item-selected {
    width: 100%;
    margin: 0;
    border-radius: 0;
    border-right: 2px solid ${({ theme }): string => theme.colors.brand.primary};
  }

  .ant-layout-sider-children {
    display: flex;
    flex-direction: column;
  }
`

const LogoWrapper = styled.div<{ isCollapsed: boolean }>`
  display: flex;
  justify-content: ${({ isCollapsed }): string =>
    isCollapsed ? 'center' : 'space-between'};
  align-items: center;
  padding: 24px 16px 16px;
  min-width: ${({ isCollapsed }): number => (isCollapsed ? 80 : 188)}px;
  flex: 0 0 auto;
`

const ToggleCollapsedButton = styled(Button)<{ $isCollapsed: boolean }>`
  svg {
    height: 16px;
    width: 16px;
  }
  border: none;
  padding: 0 !important;
  width: fit-content !important;
  height: 44px !important;
  &:hover {
    background-color: transparent !important;
  }

  display: ${({ $isCollapsed }): string => ($isCollapsed ? 'none' : 'block')};
`

const ChildrenWrapper = styled.div`
  flex: 1 1 auto;
  height: 1px;
`

export default IrmSider
